import { useState } from 'react';
import {
  AssessmentFeedbackDelivery,
  SurveyResponseData,
  SurveyResponseFeedback,
} from '../features/surveys/types';
import {
  createResponseFeedback,
  loadResponseAsNonConformity,
} from '../features/surveys/surveyResponsesApi';
import { fetchActiveFormVersion } from '../features/qms/qmsApi';

const DELIVERY_LABELS: Record<AssessmentFeedbackDelivery, string> = {
  pending: 'Pendiente de envío',
  sent: 'Notificación enviada',
  unreachable_send_manual: 'Requiere envío manual',
};

interface Props {
  surveyId: string;
  response: SurveyResponseData;
  onUpdated?: () => void;
}

export function SurveyResponseFeedbackPanel({ surveyId, response, onUpdated }: Props) {
  const [message, setMessage] = useState('');
  const [actionPlan, setActionPlan] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingNc, setLoadingNc] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showNcModal, setShowNcModal] = useState(false);
  const [ncDescription, setNcDescription] = useState('');
  const [ncIncidentDate, setNcIncidentDate] = useState('');

  const feedbackList = response.feedback ?? [];
  const hasNc = !!response.qmsRecordOpened;

  const handleSubmitFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const trimmed = message.trim();
    if (!trimmed) {
      setError('El mensaje de devolución es obligatorio');
      return;
    }

    setSubmitting(true);
    try {
      await createResponseFeedback(surveyId, response.id, {
        message: trimmed,
        actionPlan: actionPlan.trim() || null,
      });
      setMessage('');
      setActionPlan('');
      setSuccess('Devolución registrada correctamente');
      onUpdated?.();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al registrar la devolución';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoadAsNc = async () => {
    setError('');
    setSuccess('');
    setLoadingNc(true);
    try {
      const formVersion = await fetchActiveFormVersion('non_conformity');
      await loadResponseAsNonConformity(surveyId, response.id, {
        formVersionId: formVersion.id,
        description: ncDescription.trim() || undefined,
        incidentDate: ncIncidentDate || undefined,
      });
      setShowNcModal(false);
      setNcDescription('');
      setNcIncidentDate('');
      setSuccess('No conformidad creada y vinculada a la respuesta');
      onUpdated?.();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al cargar como no conformidad';
      setError(msg);
    } finally {
      setLoadingNc(false);
    }
  };

  return (
    <div className="space-y-4 p-4 bg-muted/20 rounded-lg border border-border">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold">Devolución y seguimiento</h4>
        {response.belowThreshold && (
          <span className="text-xs font-medium px-2 py-1 rounded bg-destructive/10 text-destructive">
            Bajo umbral
          </span>
        )}
      </div>

      {response.calculation && (
        <p className="text-sm text-muted-foreground">
          Puntaje total: <strong>{response.calculation.totalScore.toFixed(2)}</strong>
        </p>
      )}

      {hasNc && response.qmsRecordOpened && (
        <div className="text-sm rounded-md border border-border bg-background p-3">
          <span className="font-medium">NC vinculada: </span>
          <span className="font-mono text-xs">{response.qmsRecordOpened.openingRecordId ?? response.qmsRecordOpened.id}</span>
          <span className="text-muted-foreground"> ({response.qmsRecordOpened.status})</span>
        </div>
      )}

      {feedbackList.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Historial</p>
          {feedbackList.map((item: SurveyResponseFeedback) => (
            <div key={item.id} className="rounded-md border border-border bg-background p-3 text-sm space-y-1">
              <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                <span>{new Date(item.createdAt).toLocaleString()}</span>
                {item.author?.email && <span>· {item.author.email}</span>}
                <span>· {DELIVERY_LABELS[item.deliveryStatus]}</span>
              </div>
              <p className="whitespace-pre-wrap">{item.message}</p>
              {item.actionPlan && (
                <p className="text-muted-foreground">
                  <span className="font-medium">Plan de acción:</span> {item.actionPlan}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmitFeedback} className="space-y-3">
        <div>
          <label className="block text-xs font-medium mb-1">Mensaje de devolución *</label>
          <textarea
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px]"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Explique al destinatario los hallazgos o acciones requeridas..."
            maxLength={8000}
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Plan de acción (opcional)</label>
          <textarea
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[60px]"
            value={actionPlan}
            onChange={(e) => setActionPlan(e.target.value)}
            placeholder="Acciones correctivas o de mejora propuestas..."
            maxLength={8000}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={submitting}
          >
            {submitting ? 'Guardando...' : 'Registrar devolución'}
          </button>
          {!hasNc && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setShowNcModal(true)}
            >
              Cargar como NC
            </button>
          )}
        </div>
      </form>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {success && <p className="text-sm text-green-600">{success}</p>}

      {showNcModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-background rounded-lg border border-border shadow-lg max-w-md w-full p-4 space-y-4">
            <h3 className="text-lg font-semibold">Cargar respuesta como No Conformidad</h3>
            <p className="text-sm text-muted-foreground">
              Se creará un registro QMS de no conformidad vinculado a esta respuesta.
            </p>
            <div>
              <label className="block text-xs font-medium mb-1">Descripción (opcional)</label>
              <textarea
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px]"
                value={ncDescription}
                onChange={(e) => setNcDescription(e.target.value)}
                maxLength={8000}
                placeholder="Descripción de la no conformidad…"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Fecha del incidente (opcional)</label>
              <input
                type="date"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={ncIncidentDate}
                onChange={(e) => setNcIncidentDate(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowNcModal(false)}
                disabled={loadingNc}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleLoadAsNc}
                disabled={loadingNc}
              >
                {loadingNc ? 'Creando...' : 'Confirmar NC'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
